import React, { useState, useEffect, useCallback } from 'react';
import { Form, Input, Button, Select, DatePicker, InputNumber, Space, message, Upload, Modal, Row, Col, Statistic } from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../api';
import dayjs from 'dayjs';

const { Option } = Select;

// A custom component for a single item row
const ItemRow = ({ field, remove, materials, isInitiallyReceived, form }) => {
  const { key, ...restField } = field;
  const quantity = Form.useWatch(['items', field.name, 'quantity'], form);
  const unitPrice = Form.useWatch(['items', field.name, 'unit_price'], form);

  useEffect(() => {
    const amount = (quantity || 0) * (unitPrice || 0);
    const items = form.getFieldValue('items');
    if (items && items[field.name]) {
        items[field.name].amount = amount;
        form.setFieldsValue({ items });
    }
  }, [quantity, unitPrice, field.name, form]);
  
  const handleMaterialChange = (materialId) => {
    const selectedMaterial = materials.find(m => m.id === materialId);
    if (selectedMaterial) {
      const items = form.getFieldValue('items');
      items[field.name] = {
        ...items[field.name],
        material_id: selectedMaterial.id,
        unit_price: selectedMaterial.standard_price || 0,
      };
      form.setFieldsValue({ items: [...items] });
    }
  };

  return (
    <Row gutter={16} key={key} style={{ marginBottom: 8 }}>
      <Col span={8}>
        <Form.Item {...restField} name={[field.name, 'material_id']} rules={[{ required: true, message: '请选择物料' }]} noStyle>
          <Select placeholder="选择物料" onChange={handleMaterialChange} disabled={isInitiallyReceived} showSearch optionFilterProp="children" style={{ width: '100%' }}>
            {materials.map(material => ( <Option key={material.id} value={material.id}>{material.name} ({material.unit})</Option> ))}
          </Select>
        </Form.Item>
      </Col>
      <Col span={4}>
        <Form.Item {...restField} name={[field.name, 'quantity']} rules={[{ required: true, message: '请输入数量' }, { type: 'number', min: 0.001 }]} noStyle>
          <InputNumber placeholder="数量" min={0} precision={1} disabled={isInitiallyReceived} style={{ width: '100%' }} />
        </Form.Item>
      </Col>
      <Col span={4}>
        <Form.Item {...restField} name={[field.name, 'unit_price']} rules={[{ required: true, message: '请输入单价' }, { type: 'number', min: 0 }]} noStyle>
          <InputNumber placeholder="单价" min={0} precision={2} disabled={isInitiallyReceived} style={{ width: '100%' }} />
        </Form.Item>
      </Col>
      <Col span={4}>
        <Form.Item name={[field.name, 'amount']} noStyle>
          <InputNumber placeholder="金额" min={0} precision={2} disabled style={{ width: '100%' }} />
        </Form.Item>
      </Col>
      <Col span={4}>
        {!isInitiallyReceived && ( <MinusCircleOutlined onClick={() => remove(field.name)} /> )}
      </Col>
    </Row>
  );
};


const PurchaseOrderForm = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams();
  const [suppliers, setSuppliers] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isInitiallyReceived, setIsInitiallyReceived] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');
  const [totalAmount, setTotalAmount] = useState(0);
  const items = Form.useWatch('items', form);

  const isEditMode = !!id;
  const BUCKET_NAME = 'purchase-order-attachments';

  useEffect(() => {
    if (items) {
      const newTotal = items.reduce((sum, item) => sum + (item?.amount || 0), 0);
      setTotalAmount(newTotal);
    }
  }, [items]);

  const fetchSuppliers = useCallback(async () => {
    try {
      const response = await api.get('/v1/suppliers', { params: { pageSize: -1 } });
      setSuppliers(response.data.data);
    } catch (error) {
      message.error('获取供应商列表失败');
    }
  }, []);

  const fetchMaterials = useCallback(async () => {
    try {
      const response = await api.get('/v1/materials', { params: { pageSize: -1 } });
      setMaterials(response.data.data);
    } catch (error) {
      message.error('获取物料列表失败');
    }
  }, []);

  const fetchOrder = useCallback(async () => {
    if (isEditMode) {
      setLoading(true);
      try {
        const response = await api.get(`/v1/purchase_orders/${id}`);
        const orderData = response.data;
        form.setFieldsValue({
          ...orderData,
          order_date: dayjs(orderData.order_date),
          items: orderData.purchase_items,
        });
        if (orderData.status === 'received') {
            setIsInitiallyReceived(true);
        }

        if (orderData.attachment_urls && orderData.attachment_urls.length > 0) {
            const initialFiles = orderData.attachment_urls.map((url, index) => {
                // Extract file path from signed URL
                let filePath = url;
                try {
                    const urlObj = new URL(url);
                    const pathParts = urlObj.pathname.split('/');
                    const bucketIndex = pathParts.findIndex(part => part === 'purchase-order-attachments');
                    if (bucketIndex !== -1 && bucketIndex < pathParts.length - 1) {
                        filePath = pathParts.slice(bucketIndex + 1).join('/');
                    }
                } catch (e) {
                    console.error('Failed to parse URL:', url, e);
                }
                return {
                    uid: `-${index}`,
                    name: url.split('/').pop(),
                    status: 'done',
                    url: url,
                    path: filePath,
                };
            });
            setFileList(initialFiles);
        }
      } catch (error) {
        message.error('获取采购订单详情失败');
      } finally {
        setLoading(false);
      }
    }
  }, [isEditMode, id, form]);

  useEffect(() => {
    fetchSuppliers();
    fetchMaterials();
    if(isEditMode) {
        fetchOrder();
    }
  }, [fetchSuppliers, fetchMaterials, isEditMode, fetchOrder]);
  
  const onFinish = async (values) => {
    setLoading(true);
    try {
      const payload = {
        ...values,
        order_date: values.order_date.format('YYYY-MM-DD'),
        attachment_urls: fileList.map(file => file.path || file.url), // Save the array of paths
      };

      if (isEditMode) {
        await api.put(`/v1/purchase_orders/${id}`, payload);
        message.success('采购订单更新成功');
      } else {
        await api.post('/v1/purchase_orders', payload);
        message.success('采购订单创建成功');
      }
      navigate('/procurement/purchase-orders', { state: { closeCurrent: true } })
    } catch (error) {
      message.error(error.response?.data?.error || '操作失败');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = (status) => {
    if (isInitiallyReceived) {
        message.warn('已入库的订单不能修改状态！');
        form.setFieldsValue({ status: 'received' });
        return;
    }
    form.setFieldsValue({ status });
  };

  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file.originFileObj);
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
      });
    }
    setPreviewImage(file.url || file.preview);
    setPreviewOpen(true);
    setPreviewTitle(file.name || file.url.substring(file.url.lastIndexOf('/') + 1));
  };

  const handleChange = ({ fileList: newFileList }) => setFileList(newFileList);

  const customRequest = async ({ file, onSuccess, onError }) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('bucket', BUCKET_NAME);

    try {
      const response = await api.post('/v1/attachments/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      // Backend now returns both path and url
      file.url = response.data.url;
      file.path = response.data.path; // Store path for deletion reference
      onSuccess(response.data, file);
      message.success(`${file.name} 上传成功`);
    } catch (error) {
      onError(error);
      message.error(`${file.name} 上传失败`);
    }
  };

  const handleRemove = async (file) => {
    // The file object from AntD might not have the path if it was just uploaded
    // We rely on the full URL which should always be present for an uploaded file
    const fileUrl = file.url;
    if (fileUrl) {
      try {
        await api.delete('/v1/attachments', { data: { url: fileUrl, bucket: BUCKET_NAME } });
        message.success(`附件删除成功`);
        // Manually remove from state after successful API call
        const newFileList = fileList.filter(item => item.uid !== file.uid);
        setFileList(newFileList);
        return true;
      } catch (error) {
        message.error(`附件删除失败: ${error.response?.data?.error || '服务器错误'}`);
        return Promise.reject(false);
      }
    }
    // If no URL, it's a file that was never uploaded, just remove from UI
    const newFileList = fileList.filter(item => item.uid !== file.uid);
    setFileList(newFileList);
    return true;
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
      initialValues={{ order_date: dayjs(), status: 'pending', items: [{}], attachment_urls: [] }}
    >
      <Form.Item name="order_no" label="订单号" hidden={!isEditMode}>
        <Input readOnly />
      </Form.Item>

      <Row gutter={16}>
        <Col span={8}>
          <Form.Item name="supplier_id" label="供应商" rules={[{ required: true }]}>
            <Select placeholder="选择供应商" disabled={isInitiallyReceived}>
              {suppliers.map(supplier => ( <Option key={supplier.id} value={supplier.id}>{supplier.name}</Option>))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="order_date" label="订单日期" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} disabled={isInitiallyReceived} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="status" label="订单状态" rules={[{ required: true }]}>
            <Select onChange={handleStatusChange} disabled={isInitiallyReceived}>
              <Option value="pending">待入库</Option>
              <Option value="received">已入库</Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Form.Item name="note" label="备注">
        <Input.TextArea rows={3} disabled={isInitiallyReceived} />
      </Form.Item>

      <h3>订单明细</h3>
      <div style={{ padding: '8px 0' }}>
        <Row gutter={16}>
          <Col span={8}><strong>物料名称</strong></Col>
          <Col span={4}><strong>数量</strong></Col>
          <Col span={4}><strong>单价</strong></Col>
          <Col span={4}><strong>金额</strong></Col>
          <Col span={4}><strong>操作</strong></Col>
        </Row>
      </div>
      <Form.List name="items">
        {(fields, { add, remove }) => (
          <>
            {fields.map(field => (
              <ItemRow key={field.key} field={field} remove={remove} materials={materials} isInitiallyReceived={isInitiallyReceived} form={form} />
            ))}
            {!isInitiallyReceived && (
              <Form.Item>
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                  添加物料
                </Button>
              </Form.Item>
            )}
          </>
        )}
      </Form.List>
      
      <Row style={{ marginTop: 16 }}>
        <Col>
          <Statistic title="订单总金额" value={totalAmount} prefix="¥" precision={2} />
        </Col>
      </Row>

      <h3>附件</h3>
      <Form.Item>
        <Upload
          customRequest={customRequest}
          fileList={fileList}
          onPreview={handlePreview}
          onChange={handleChange}
          onRemove={handleRemove}
          listType="picture-card"
          disabled={isInitiallyReceived}
        >
          {!isInitiallyReceived && fileList.length < 5 && ( <div><PlusOutlined /><div style={{ marginTop: 8 }}>上传</div></div> )}
        </Upload>
      </Form.Item>
      <Modal open={previewOpen} title={previewTitle} footer={null} onCancel={() => setPreviewOpen(false)}>
        <img alt="preview" style={{ width: '100%' }} src={previewImage} />
      </Modal>

      <Form.Item>
        <Space>
          <Button type="primary" htmlType="submit" loading={loading} disabled={isInitiallyReceived}>
            {isEditMode ? '更新' : '创建'}
          </Button>
          <Button onClick={() => navigate('/procurement/purchase-orders', { state: { closeCurrent: true } })}>
            返回
          </Button>
        </Space>
      </Form.Item>
    </Form>
  );
};

export default PurchaseOrderForm;